import React, { useMemo, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTransactionService, DetailsType } from '../../services/transaction.service';
import { QUERY_KEYS } from '../../utils/queryKeys';
import { useAuth } from '../../context/AuthContext';
import { useRecalculation } from '../../context/RecalculationStatusContext';
import { useNotification } from '../../context/NotificationContext';
import { TaxableTransaction } from '../tax-reports/types';
import SmartCheckOverview from './SmartCheckOverview';
import SmartCheckDetailsPage from './SmartCheckDetailsPage';
import AICheckPage from './AICheckPage';
import {
    useIgnoreIssueMutation,
    useUnignoreIssueMutation,
    useIgnoredIssues,
} from './hooks/useIgnoredIssues';

interface IssueTransaction extends TaxableTransaction {
    issueType?: 'missingAssetValue' | 'costBasis';
    taxYear?: number;
}

const SmartCheckPage: React.FC = () => {
    const { currentCtUser, currentUser } = useAuth();
    const transactionService = useTransactionService();
    const { status: recalculationStatus } = useRecalculation();
    const { showNotification } = useNotification();
    const queryClient = useQueryClient();
    const isRecalculating = !!recalculationStatus?.run || !!recalculationStatus?.new;

    const [processingSavedTransactions, setProcessingSavedTransactions] = useState<Set<string | number>>(new Set());
    const [ignoringIssues, setIgnoringIssues] = useState<Set<string | number>>(new Set());

    // Clear processing transactions when recalculation completes
    useEffect(() => {
        if (!isRecalculating && processingSavedTransactions.size > 0) {
            setProcessingSavedTransactions(new Set());
        }
    }, [isRecalculating, processingSavedTransactions.size]);

    // Get all issues data
    const {
        data: allIssuesResponse,
        isLoading: isLoadingAllIssues,
        isFetching: isFetchingAllIssues,
    } = useQuery({
        queryKey: [QUERY_KEYS.ALL_TIME_ISSUES],
        queryFn: () => transactionService.getAllTimeIssueCount(DetailsType.ONLY_ISSUES),
    });

    const allIssuesData = useMemo(() => {
        if (!allIssuesResponse) return [];

        let costBasisIssues: any[] = [];
        let missingPriceIssues: any[] = [];

        if (typeof allIssuesResponse === 'object' && !Array.isArray(allIssuesResponse)) {
            costBasisIssues = allIssuesResponse.missingCostBasisTransactions || [];
            missingPriceIssues = allIssuesResponse.missingPriceIncomeTransactions || [];
        }

        const processedCostBasisIssues = costBasisIssues.map(tx => ({
            ...tx,
            issueType: 'costBasis' as const,
            taxYear: tx.taxYear || new Date(Array.isArray(tx.sold_at) ? tx.sold_at[0] : tx.sold_at).getFullYear()
        }));

        const processedMissingPriceIssues = missingPriceIssues.map(tx => ({
            ...tx,
            currency: tx.buy_coin,
            amount: tx.buy,
            issueType: 'missingAssetValue' as const,
            taxYear: tx.taxYear || new Date(tx.time).getFullYear(),
            acquired_at: tx.time,
            sold_at: tx.time,
            gain: 0,
            cost_basis: 0,
            proceeds: tx.buy_value || 0,
            sale_exchange: tx.origin || 'N/A',
            id: tx.id || tx.time
        }));

        const allIssues = [...processedCostBasisIssues, ...processedMissingPriceIssues];

        return allIssues.sort((a, b) => {
            const dateA = a.issueType === 'costBasis' ? a.sold_at : a.acquired_at;
            const dateB = b.issueType === 'costBasis' ? b.sold_at : b.acquired_at;
            const timestampA = new Date(Array.isArray(dateA) ? dateA[0] : dateA).getTime();
            const timestampB = new Date(Array.isArray(dateB) ? dateB[0] : dateB).getTime();
            return timestampA - timestampB;
        });
    }, [allIssuesResponse]);

    const userId = currentUser?.uid || '';
    const ignoreIssueMutation = useIgnoreIssueMutation();
    const unignoreIssueMutation = useUnignoreIssueMutation();
    const { data: allIgnoredIssues } = useIgnoredIssues();

    // Filter out stale ignored issues (issues that have been resolved)
    // This prevents showing ignored badges for issues that no longer exist
    const activeIgnoredIssues = useMemo(() => {
        if (!allIgnoredIssues || !allIssuesData.length) return allIgnoredIssues || {};

        const currentIssueIds = new Set(allIssuesData.map(i => i.id?.toString()));

        // Only include ignored issues that still exist in current issues
        return Object.entries(allIgnoredIssues)
            .filter(([issueId]) => currentIssueIds.has(issueId))
            .reduce((acc, [id, data]) => ({ ...acc, [id]: data }), {});
    }, [allIgnoredIssues, allIssuesData]);

    const activeIgnoredIssuesCount = activeIgnoredIssues ? Object.keys(activeIgnoredIssues).length : 0;

    // Calculate issue statistics (excluding ignored issues)
    const issueStatistics = useMemo(() => {
        // Filter out ignored issues first
        const activeIssues = allIssuesData.filter(issue => {
            return !(activeIgnoredIssues && issue.id?.toString() in activeIgnoredIssues);
        });

        const costBasisIssues = activeIssues.filter(issue => issue.issueType === 'costBasis');
        const missingPriceIssues = activeIssues.filter(issue => issue.issueType === 'missingAssetValue');

        return {
            costBasisIssues: costBasisIssues.map(issue => ({
                gain: issue.gain || 0,
                currency: issue.currency || '',
                taxYear: issue.taxYear
            })),
            missingPriceIssues: missingPriceIssues.map(issue => ({
                currency: issue.currency || '',
                taxYear: issue.taxYear
            })),
        };
    }, [allIssuesData, activeIgnoredIssues]);

    // Extract ignored issues data with currency and taxYear
    const ignoredIssuesData = useMemo(() => {
        if (!activeIgnoredIssues) return [];

        return Object.entries(activeIgnoredIssues).map(([issueId, ignoredIssue]) => {
            // Try to find the original issue to get taxYear
            const originalIssue = allIssuesData.find(i => i.id?.toString() === issueId);

            return {
                currency: ignoredIssue.currency,
                taxYear: originalIssue?.taxYear,
            };
        });
    }, [activeIgnoredIssues, allIssuesData]);

    // Calculate categorized ignored issues counts
    const categorizedIgnoredCounts = useMemo(() => {
        if (!activeIgnoredIssues) return { high: 0, low: 0, pricing: 0 };

        let highCount = 0;
        let lowCount = 0;
        let pricingCount = 0;

        Object.keys(activeIgnoredIssues).forEach(issueId => {
            const originalIssue = allIssuesData.find(i => i.id?.toString() === issueId);
            if (originalIssue) {
                if (originalIssue.issueType === 'missingAssetValue') {
                    pricingCount++;
                } else if (originalIssue.issueType === 'costBasis') {
                    const gain = Math.abs(originalIssue.gain || 0);
                    if (gain > 10) {
                        highCount++;
                    } else {
                        lowCount++;
                    }
                }
            }
        });

        return { high: highCount, low: lowCount, pricing: pricingCount };
    }, [activeIgnoredIssues, allIssuesData]);

    // Handle ignore/unignore
    const handleIgnoreIssue = async (
        transactionId: string | number,
        issueType: string,
        currency: string,
        taxYear?: number
    ) => {
        if (!userId) return;

        setIgnoringIssues(prev => new Set(prev).add(transactionId));

        try {
            const issue = allIssuesData.find(i => i.id === transactionId);
            if (!issue) return;

            const isCurrentlyIgnored = activeIgnoredIssues && transactionId.toString() in activeIgnoredIssues;

            if (isCurrentlyIgnored) {
                await unignoreIssueMutation.mutateAsync({
                    issueId: transactionId.toString(),
                    taxYear: issue.taxYear?.toString() || '',
                });
                showNotification('Issue unignored successfully', 'success');
            } else {
                await ignoreIssueMutation.mutateAsync({
                    issueId: transactionId.toString(),
                    taxYear: taxYear?.toString() || '',
                    issueType: issueType as 'costBasis' | 'missingAssetValue',
                    currency,
                });
                showNotification('Issue ignored successfully', 'success');
            }
        } catch (error) {
            console.error('Error toggling ignore status:', error);
            showNotification('Failed to update issue status', 'error');
        } finally {
            setIgnoringIssues(prev => {
                const newSet = new Set(prev);
                newSet.delete(transactionId);
                return newSet;
            });
        }
    };

    const isIssueIgnored = (transactionId: string | number, taxYear?: number): boolean => {
        return !!(activeIgnoredIssues && transactionId.toString() in activeIgnoredIssues);
    };

    return (
        <Routes>
            <Route
                index
                element={
                    <SmartCheckOverview
                        costBasisIssues={issueStatistics.costBasisIssues}
                        missingPriceIssues={issueStatistics.missingPriceIssues}
                        ignoredIssuesCount={activeIgnoredIssuesCount}
                        ignoredIssuesData={ignoredIssuesData}
                        highIgnoredCount={categorizedIgnoredCounts.high}
                        lowIgnoredCount={categorizedIgnoredCounts.low}
                        pricingIgnoredCount={categorizedIgnoredCounts.pricing}
                        userCurrency={currentCtUser?.currency || 'USD'}
                        isLoading={isLoadingAllIssues || isFetchingAllIssues}
                    />
                }
            />
            <Route
                path="issues"
                element={
                    <SmartCheckDetailsPage
                        allIssuesData={allIssuesData}
                        userCurrency={currentCtUser?.currency || 'USD'}
                        isLoading={isLoadingAllIssues || isFetchingAllIssues}
                        isRecalculating={isRecalculating}
                        processingSavedTransactions={processingSavedTransactions}
                        ignoringIssues={ignoringIssues}
                        allIgnoredIssues={activeIgnoredIssues || {}}
                        ignoredIssuesCount={activeIgnoredIssuesCount}
                        highIgnoredCount={categorizedIgnoredCounts.high}
                        lowIgnoredCount={categorizedIgnoredCounts.low}
                        pricingIgnoredCount={categorizedIgnoredCounts.pricing}
                        onIgnore={handleIgnoreIssue}
                        isIgnored={isIssueIgnored}
                    />
                }
            />
            <Route
                path="issues/ai-check/:issueId"
                element={
                    <AICheckPage
                        allIssuesData={allIssuesData}
                        userCurrency={currentCtUser?.currency || 'USD'}
                    />
                }
            />
        </Routes>
    );
};

export default SmartCheckPage;
